import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useChatBot } from '@/contexts/ChatBotContext'
import { ChatMessage } from './ChatMessage'
import { ChatInput } from './ChatInput'
import { QuickSuggestions } from './QuickSuggestions'
import { quickSuggestions } from './config'
import type { ChatMessage as ChatMessageType, ChatState } from '@brochure/shared'

const CHATBOT_HIDDEN_PATHS = ['/area-riservata', '/admin', '/login']

/**
 * Bebobot head icon — a friendly, antennaed bot face used as the chat toggle.
 * Antennae end in tiny leaves as a nod to Maia e Tas's natural, green identity.
 */
function BotHeadIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      {/* Left antenna with leaf */}
      <path
        d="M7.5 4.5C7.5 4.5 7 6 7.5 7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M6.5 3.5C6.5 3.5 8 2.5 8.5 3.5C9 4.5 7.5 5.5 6.5 5C5.5 4.5 6.5 3.5 6.5 3.5Z"
        fill="currentColor"
      />

      {/* Right antenna with leaf */}
      <path
        d="M16.5 4.5C16.5 4.5 17 6 16.5 7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M17.5 3.5C17.5 3.5 16 2.5 15.5 3.5C15 4.5 16.5 5.5 17.5 5C18.5 4.5 17.5 3.5 17.5 3.5Z"
        fill="currentColor"
      />

      {/* Head */}
      <rect
        x="4"
        y="6.5"
        width="16"
        height="13"
        rx="5"
        stroke="currentColor"
        strokeWidth="1.75"
      />

      {/* Eyes */}
      <circle cx="9.5" cy="11.5" r="1.25" fill="currentColor" />
      <circle cx="14.5" cy="11.5" r="1.25" fill="currentColor" />

      {/* Smile */}
      <path
        d="M9.5 15C10.5 16.1667 13.5 16.1667 14.5 15"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function ChatBot() {
  const { t, i18n } = useTranslation('common')
  const { isOpen, setIsOpen } = useChatBot()
  const location = useLocation()

  const hideChatBot = CHATBOT_HIDDEN_PATHS.some((path) =>
    location.pathname.includes(path)
  )

  const createWelcomeMessage = useCallback((): ChatMessageType => {
    return {
      id: 'welcome',
      role: 'assistant',
      content: t('chatbot.welcome'),
      timestamp: new Date(),
    }
  }, [t])

  const [messages, setMessages] = useState<ChatMessageType[]>(() => [
    createWelcomeMessage(),
  ])
  const [chatState, setChatState] = useState<ChatState>('idle')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, scrollToBottom])

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300)
    }
  }, [isOpen])

  // Keep the welcome message in sync with the selected language.
  useEffect(() => {
    setMessages((prev) => {
      if (prev.length === 1 && prev[0].id === 'welcome') {
        return [createWelcomeMessage()]
      }
      return prev
    })
  }, [createWelcomeMessage])

  if (hideChatBot) return null

  const generateId = () =>
    `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`

  const sendMessage = async (content: string) => {
    if (!content.trim() || chatState === 'loading') return

    const userMessage: ChatMessageType = {
      id: generateId(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMessage])
    setChatState('loading')

    try {
      const apiUrl = import.meta.env.VITE_API_URL || ''
      const response = await fetch(`${apiUrl}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: content,
          history: messages.map((m) => ({ role: m.role, content: m.content })),
          language: i18n.language,
        }),
      })

      const data = await response.json()

      const assistantMessage: ChatMessageType = {
        id: generateId(),
        role: 'assistant',
        content: data.response || data.error || t('chatbot.error'),
        timestamp: new Date(data.timestamp || Date.now()),
      }

      setMessages((prev) => [...prev, assistantMessage])
      setChatState('idle')
    } catch (error) {
      console.error('Chat error:', error)
      setChatState('error')

      const errorMessage: ChatMessageType = {
        id: generateId(),
        role: 'assistant',
        content: t('chatbot.error'),
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, errorMessage])
    }
  }

  const handleReset = () => {
    setMessages([createWelcomeMessage()])
    setChatState('idle')
  }

  const isAtWelcome =
    messages.length === 1 && messages[0].id === 'welcome'

  const handleSuggestionSelect = (messageKey: string) => {
    const message = t(messageKey)
    sendMessage(message)
  }

  return (
    <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6" style={{ zIndex: 9999 }}>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.92 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
              'absolute bottom-20 right-0',
              'w-[calc(100vw-32px)] sm:w-[400px] max-w-[400px]',
              'bg-cream border border-cord/40 rounded-[28px]',
              'shadow-2xl shadow-ink/10',
              'flex flex-col',
              'max-h-[calc(100vh-100px)] sm:max-h-[calc(100vh-120px)]',
              'overflow-hidden'
            )}
          >
            {/* Header */}
            <div className="bg-gradient-to-br from-maia to-maia-dark text-white px-5 py-4 flex items-center justify-between shrink-0">
              <div>
                <h3 className="font-display font-semibold text-lg leading-tight">
                  {t('chatbot.title')}
                </h3>
                <p className="text-xs text-white/80 font-medium">
                  {t('chatbot.subtitle')}
                </p>
              </div>
              <button
                onClick={handleReset}
                className="p-2.5 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                aria-label={t('chatbot.newConversation')}
                title={t('chatbot.newConversation')}
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-5 min-h-[200px] max-h-[420px] bg-maia-warm/40">
              {messages.map((message) => (
                <ChatMessage key={message.id} message={message} />
              ))}

              {isAtWelcome && quickSuggestions.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="pt-2"
                >
                  <QuickSuggestions
                    suggestions={quickSuggestions}
                    onSelect={handleSuggestionSelect}
                    disabled={chatState === 'loading'}
                  />
                </motion.div>
              )}

              {chatState === 'loading' && (
                <ChatMessage
                  message={{
                    id: 'typing',
                    role: 'assistant',
                    content: '',
                    timestamp: new Date(),
                  }}
                  isTyping
                />
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <ChatInput
              ref={inputRef}
              onSend={sendMessage}
              disabled={chatState === 'loading'}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className={cn(
          'w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center',
          'bg-maia text-white shadow-xl shadow-maia/25',
          'hover:bg-maia-dark transition-colors duration-300',
          'border-2 border-white/20',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-maia'
        )}
        aria-label={isOpen ? t('chatbot.close') : t('chatbot.open')}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <X className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
            </motion.div>
          ) : (
            <motion.div
              key="chat"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <BotHeadIcon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  )
}
